import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { gaps } from "../../../primitives/spacing";
import { Body } from "../../../primitives/data-display/Body";
import { AuthFormFrame } from "../AuthLayout";

interface ForgotPasswordFormProps {
  email: string;
  setEmail: (v: string) => void;
  sent: boolean;
  loading: boolean;
  error: string | null;
  onSubmit: (e: React.FormEvent) => void | Promise<void>;
  backToLoginHref: string;
}

/** Shared forgot-password form. Renders a sent-confirmation state after submission. */
export function ForgotPasswordForm({
  email,
  setEmail,
  sent,
  loading,
  error,
  onSubmit,
  backToLoginHref,
}: ForgotPasswordFormProps) {
  const { t } = useTranslation();

  return (
    <AuthFormFrame
      title={t("auth.forgotPasswordPage.title")}
      subtitle={t("auth.forgotPasswordPage.subtitle")}
      footer={
        <Button component={Link} to={backToLoginHref} variant="text" size="small">
          {t("auth.forgotPasswordPage.backToLogin")}
        </Button>
      }
    >
      {sent ? (
        <Stack gap={gaps.base}>
          <Alert severity="success">{t("auth.forgotPasswordPage.sent")}</Alert>
          <Body>{t("auth.forgotPasswordPage.sentDetail")}</Body>
        </Stack>
      ) : (
        <>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack component="form" onSubmit={onSubmit} gap={gaps.base}>
            <TextField
              label={t("auth.email")}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              autoFocus
              autoComplete="email"
              size="small"
            />
            <Button type="submit" variant="contained" fullWidth disabled={loading || !email.trim()}>
              {loading ? t("auth.forgotPasswordPage.sending") : t("auth.forgotPasswordPage.send")}
            </Button>
          </Stack>
        </>
      )}
    </AuthFormFrame>
  );
}
